/**
 * 商品カードの「タグ枠」と「価格枠」を、グリッド/カルーセルの行単位で揃える。
 *
 * 固定行数で高さを確保すると、その行のカードが全員1行（またはタグ無し）でも
 * 予約ぶんの空白が残ってしまう。ここでは
 *   1. いったん高さ指定を外して自然高さに戻す
 *   2. カードの上端 Y でグリッドの行にグルーピングする
 *   3. 行ごとの最大高さを、その行のカードだけに適用する
 * という順で処理するため、行によって 0px / 1行 / 2行 と可変になり、
 * 横に並んだカードの商品名の開始位置は必ず揃う。
 *
 * 価格枠は「下端」で揃える。セール品は［参考価格］［販売価格］の2行になるため、
 * 通常価格のカードは上に余白を足して底で合わせる。こうすると販売価格の行と、
 * その下のカラーチップが横一列で揃う。
 *
 * ## どこを CSS に渡したか（#448 / #418）
 *
 * コレクション / 検索のグリッド（`.product-grid--grid`）は、親 `ul` が行トラックを
 * 持っているのでカードを subgrid にすれば段が自動で揃う。
 * `snippets/product-card-row-align-css.liquid` がそれをやるので、**このスクリプトは
 * グリッドの中を一切測らない**（`isCssAligned`）。無限スクロールで168枚まで増やしても
 * 段揃えは 0px ずれ・書き込み 0 件（本番 DOM で実測）。
 *
 * TOP のカルーセルは `slideshow-slides` が flex で、カードが行トラックを共有しないため
 * subgrid にできない。ランキングのパネル横断の高さ合わせも同様。ここは計測方式のまま残す。
 *
 * ## 読み書きの順序（強制レイアウトを減らすための設計）
 *
 * 計測（読み）と高さの適用（書き）を交互にやると、そのたびにブラウザがレイアウトを
 * 取り直す。1パスを「書き→読み」の境界が3回だけになるように並べている。
 *
 *   R0: 内容の指紋を読む。変わっていない行はここで丸ごと落とす（書き込みゼロで終わる）
 *   W1: 対象の行だけ、タグ枠・価格枠・ランキングカードの指定を外す
 *   R1: カード上端 / タグ枠の scrollHeight / 価格枠の scrollHeight と行オフセット を全部読む
 *   W2: タグ枠と価格枠の高さを書く
 *   R2: ランキングカードの高さを読む（W2 の結果を含んだ高さが必要なのでここ）
 *   W3: ランキングカードの min-height を書く
 *
 * 同じ rect / getComputedStyle を関数ごとに読み直さないよう、読みは1パス内で
 * キャッシュする（`readEpoch` を進めた時点で無効になる）。
 *
 * ## なぜこの形にしたか（本番TOP・CPU 4倍・SP 390px で実測）
 *
 * 改修前は1回の読み込みでこのスクリプトが**レイアウトを556回**起こしていた
 * （ページ全体で632回）。原因は3つで、いずれも「同じものを何度も測る」形だった。
 *
 *   1. 同じカードの rect を alignTags / alignPrices / alignRankingCards /
 *      elementsInPendingRows がそれぞれ読み直していた
 *   2. `capOf` がカード1枚ごとに getComputedStyle を呼んでいた（設定はコンテナ単位で同じ）
 *   3. load / fonts.ready / 保険のタイマー3本 / アプリの遅延描画で8回前後走るのに、
 *      毎回リセット→計測→書き込みを最初からやり直していた（2回目以降はほぼ空回り）
 */
const TAGS_SELECTOR = '.product-card-tags-block';
const PRICE_SELECTOR = 'product-price';

/** 揃えの対象（タグ枠・価格枠）をまとめたセレクタ。監視の絞り込みに使う */
const ALIGN_TARGET_SELECTOR = `${TAGS_SELECTOR}, ${PRICE_SELECTOR}`;

/**
 * カルーセル（ランキング等）の `.resource-list__item` も行の単位になる。
 * ここから漏れていると行の判定がカード内部の要素基準になり、
 * 1枚だけ別の行として扱われることがある。
 */
const CARD_SELECTOR =
  '.product-grid__item, .resource-list__item, .product-card__wrapper, product-card';

/**
 * 行の一致判定はこのコンテナ単位で行う。
 * カードの親要素はカードごとに別インスタンスなので、キーには使えない。
 */
const CONTAINER_SELECTOR = '.product-grid, .resource-list, .resource-list__carousel, ul, ol';

/**
 * CSS（subgrid）が段揃えを担当するコンテナ。ここの中は JS が一切測らない。
 *
 * コレクション / 検索のグリッドは `snippets/product-card-row-align-css.liquid` が
 * `ul.product-grid` の行トラックをカードに共有させて段を揃える（#448）。
 * 高さを測る必要が無くなるので、対象からも監視からも外して強制レイアウトをゼロにする（#418）。
 *
 * 千鳥配置（`.product-grid--organic`）は CSS 側の対象外なので、ここでも除外しない。
 */
const CSS_ALIGNED_CONTAINER_SELECTOR = '.product-grid--grid';

/**
 * `subgrid` に対応しているか。非対応ブラウザでは CSS 側の `@supports` が丸ごと落ちるので、
 * 従来どおり JS が測って揃える。
 */
const cssRowAlignSupported =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('grid-template-rows', 'subgrid');

/**
 * CSS が段揃えを担当している領域の要素か。
 * @param {Element} element
 * @returns {boolean}
 */
function isCssAligned(element) {
  if (!cssRowAlignSupported) return false;
  return element.closest?.(CSS_ALIGNED_CONTAINER_SELECTOR) != null;
}

/** ランキングのパネル横断コンテナ。段の判定をパネル単位にしないためのもの */
const RANKING_PANELS_SELECTOR = '.collection-tabs__panels';

/** ランキングカード（パネル横断で段を揃える対象） */
const RANKING_CARD_SELECTOR = `${RANKING_PANELS_SELECTOR} .resource-list__item`;

/**
 * 同じ行と見なす上端 Y の許容差（px）。端数やサブピクセルで 1〜2px ずれる。
 */
const ROW_TOLERANCE = 6;

/** ランキングの段判定の許容差（px）。段間は約280px空くので広く取れる */
const RANKING_ROW_TOLERANCE = 40;

/**
 * 想定する最大列数。追加読み込みの差分だけを揃えるとき、
 * 同じ行の既存カードを取り逃さないための余裕として使う。
 */
const MAX_COLUMNS = 12;

/**
 * 連続した変化をまとめる最小間隔（ms）。
 *
 * Swym（お気に入り）はカード1枚ずつにボタンを注入し、そのたびに style を書き込む。
 * TOP はカードが 100枚超あるため、rAF ごとに再計算すると計測と書き込みが数十回繰り返され、
 * メインスレッドが数秒占有される（実測 2.5秒）。最後の状態だけ揃えばよいので束ねる。
 */
const MIN_INTERVAL = 150;

/**
 * 直近の再整列を「まだ高さを書き込んでいない行」だけに絞れるか。
 *
 * 無限スクロールはカードを既存の `ul.product-grid` に足すだけなので、
 * 全カードを測り直す必要はない。追加だけの変化ではこのフラグが立ち、
 * 既に揃っている行の測り直し（reset → scrollHeight → 書き込み）を省く。
 * 差し替え・削除を含む変化や、resize・フォント読み込みでは全体を測り直す。
 */
let appendOnly = false;

/** @type {number | null} */
let frame = null;

/** @type {number | null} */
let timer = null;

let lastRun = 0;

/** @type {MutationObserver | null} */
let observer = null;

/** @type {ResizeObserver | null} */
let resizeObserver = null;

/** 直前に書き込んだ高さ。同じ値なら書き込まず、監視の再発火を防ぐ @type {WeakMap<Element, string>} */
const appliedHeight = new WeakMap();

/**
 * 直近に測ったときの「内容の指紋」。次のパスで同じなら測り直さない。
 *
 * 1回の読み込みでこのスクリプトは8回前後走る（load / fonts.ready / 保険のタイマー3本 /
 * アプリの遅延描画）。毎回リセット→計測→書き込みをやり直していたため、
 * **2回目以降はほぼ同じ値を書き直すだけの空回り**になっていた。
 *
 * 指紋には高さを入れない。高さはこのスクリプト自身が書き込むので、
 * 入れると「書いた→指紋が変わった→また測る」で永久に落ち着かない。
 *
 * @type {WeakMap<Element, string>}
 */
const measuredSignature = new WeakMap();

/**
 * 次のパスで指紋の一致を無視して全対象を測り直すフラグ。
 *
 * 指紋（幅・子要素数・文字数）は「フォントの差し替えで文字幅だけが変わり
 * 折返し行数が増減する」変化を拾えない。タグ枠の高さは JS が固定するため
 * ResizeObserver（カード実寸）にも現れず、一度小さく確定した高さが
 * 二度と測り直されずタグ下枠線がクリップされたままになる（SP実機で報告）。
 * fonts.ready 後の1パスだけ全量で測り直して取りこぼしを塞ぐ。
 */
let forceRemeasure = false;

/**
 * ResizeObserver が実寸の変化を報告したカード。
 * 指紋では拾えない変化（画像のデコード、スウォッチの折返し、フォントの差し替え）が
 * ここに入る。パスの先頭で読んで空にする。
 * @type {Set<Element>}
 */
const resizedCards = new Set();

/**
 * 読みのキャッシュ世代。書き込みを挟んだら進める。
 * 進めた時点で rect / computed style のキャッシュは無効になる。
 */
let readEpoch = 0;

/** @type {Map<Element, number>} 世代内のカード上端 Y */
let cardTopCache = new Map();

/** @type {Map<Element, number>} 世代内のコンテナ別のタグ枠上限高さ */
let capCache = new Map();

/**
 * 書き込みの直後に呼ぶ。以降の読みは新しいレイアウトで取り直す。
 */
function invalidateReads() {
  readEpoch += 1;
  cardTopCache = new Map();
  capCache = new Map();
}

/**
 * パスの開始時に呼ぶ。
 */
function beginPass() {
  invalidateReads();
}

/**
 * タグ枠・価格枠の属するカード要素を返す
 * @param {Element} element
 * @returns {Element}
 */
function cardOf(element) {
  return element.closest(CARD_SELECTOR) ?? element.parentElement ?? element;
}

/** @type {WeakMap<Element, string>} */
const containerKeys = new WeakMap();
let containerSeq = 0;

/**
 * @param {Element} container
 * @returns {string}
 */
function containerKey(container) {
  let key = containerKeys.get(container);
  if (!key) {
    key = `c${(containerSeq += 1)}`;
    containerKeys.set(container, key);
  }
  return key;
}

/**
 * 行の判定に使うコンテナ。
 * ランキングはパネル(コレクション)横断で同じ段の高さを揃える。
 * パネル単位で揃えるとセール表示等の1行差で隣のパネルと段位置がズレ、
 * スワイプの継ぎ目で崩れて見える。
 * @param {Element} card
 * @returns {Element}
 */
function rowContainerOf(card) {
  return card.closest(RANKING_PANELS_SELECTOR) ?? card.closest(CONTAINER_SELECTOR) ?? document.body;
}

/**
 * カード上端 Y。同じ世代のあいだキャッシュする。
 *
 * alignTags / alignPrices / alignRankingCards / elementsInPendingRows が
 * それぞれ rect を読み直していたため、1パスで同じカードを4〜5回測っていた。
 *
 * @param {Element} card
 * @returns {number}
 */
function topOf(card) {
  let top = cardTopCache.get(card);
  if (top === undefined) {
    top = card.getBoundingClientRect().top + window.scrollY;
    cardTopCache.set(card, top);
  }
  return top;
}

/**
 * 同じコンテナで上端がほぼ同じものを1行としてまとめる。
 *
 * 以前は上端を `Math.round(top / 2)` でキー化していたが、丸めの境界をまたぐと
 * 1px 差でも別の行に分かれる（「たまに1枚だけ上にずれる」の原因）。
 * ソートしてから許容差でクラスタリングする。
 *
 * @param {Element[]} elements
 * @param {number} [tolerance]
 * @returns {Element[][]}
 */
function groupByRow(elements, tolerance = ROW_TOLERANCE) {
  /** @type {Map<string, {element: Element, top: number}[]>} */
  const byContainer = new Map();

  for (const element of elements) {
    const card = cardOf(element);
    const container = rowContainerOf(card);
    const top = topOf(card);
    const key = containerKey(container);
    const list = byContainer.get(key);

    if (list) list.push({ element, top });
    else byContainer.set(key, [{ element, top }]);
  }

  /** @type {Element[][]} */
  const rows = [];

  for (const list of byContainer.values()) {
    list.sort((a, b) => a.top - b.top);

    /** @type {Element[]} */
    let bucket = [];
    let base = Number.NaN;

    for (const item of list) {
      if (bucket.length === 0) {
        base = item.top;
        bucket.push(item.element);
      } else if (item.top - base <= tolerance) {
        bucket.push(item.element);
      } else {
        rows.push(bucket);
        bucket = [item.element];
        base = item.top;
      }
    }

    if (bucket.length > 0) rows.push(bucket);
  }

  return rows;
}

/**
 * まだ高さを書き込んでいない要素を含む行の、全要素を返す。
 *
 * 行の最大高さを求めるには同じ行の全要素が必要なので、未処理の要素だけでは足りない。
 * 行に1つでも未処理があればその行はまとめて処理し、全部処理済みの行は触らない。
 *
 * @param {Element[]} elements
 * @returns {Element[]}
 */
function elementsInPendingRows(elements) {
  // 未処理の要素が現れる位置から後ろだけを見る。行の相方を取り逃さないよう
  // 1行ぶん（最大12列とみなす）手前から含める。
  // 全要素で行判定すると、追加読み込みのたびに全カードぶんの rect 読みが走る
  // （実測でページあたり 23,000 回）。
  const firstPending = elements.findIndex((element) => !appliedHeight.has(element));
  if (firstPending === -1) return [];

  const scope = elements.slice(Math.max(0, firstPending - MAX_COLUMNS));
  const pending = [];

  for (const bucket of groupByRow(scope)) {
    if (bucket.some((element) => !appliedHeight.has(element))) pending.push(...bucket);
  }

  return pending;
}

/**
 * 最大行数から許容高さを求める。CSS カスタムプロパティを正とする。
 *
 * `--tags-rows` / `--tags-row-height` / row-gap / padding はコンテナ単位の設定なので、
 * カード1枚ずつ getComputedStyle を呼ぶ必要はない。同じ行コンテナの中では同じ値になる。
 *
 * @param {Element} tags
 * @returns {number}
 */
function capOf(tags) {
  const key = rowContainerOf(cardOf(tags));
  const cached = capCache.get(key);
  if (cached !== undefined) return cached;

  const style = getComputedStyle(tags);
  const rows = Number.parseFloat(style.getPropertyValue('--tags-rows')) || 2;
  const rowHeight = Number.parseFloat(style.getPropertyValue('--tags-row-height')) || 17;
  const gap = Number.parseFloat(style.rowGap) || 6;
  const padding =
    (Number.parseFloat(style.paddingTop) || 0) + (Number.parseFloat(style.paddingBottom) || 0);

  const cap = rows * rowHeight + (rows - 1) * gap + padding;
  capCache.set(key, cap);
  return cap;
}

/**
 * 同じ値なら書き込まない。書き込みが監視を再発火させて往復するのを防ぐ。
 * @param {Element} element
 * @param {string} height
 */
function setHeight(element, height) {
  if (appliedHeight.get(element) === height) return;

  /** @type {HTMLElement} */ (element).style.height = height;
  appliedHeight.set(element, height);
}

/**
 * 対象になりうる要素。カードの中にあるかだけを見る（レイアウトは読まない）。
 *
 * 行揃えは商品カード内の要素だけが対象。PDPのタグブロック（blocks/product-tags.liquid）を
 * 巻き込むと、カード外なのに高さを書き込まれて折返し2行目がはみ出す。
 *
 * 表示・非表示の判定はここではやらない。`signatureOf` が読む `clientWidth` が
 * そのまま判定を兼ねる（非表示なら 0）。以前は要素ごとに `offsetParent` を読んでいて、
 * `CARD_SELECTOR` が1枚のカードで複数の祖先に一致するぶんも重なり、
 * **何も変わっていないパスでも1回あたり184回**読んでいた。
 *
 * @param {string} selector
 * @returns {Element[]}
 */
function cardTargets(selector) {
  /** @type {Element[]} */
  const found = [];

  for (const element of document.querySelectorAll(selector)) {
    if (element.closest(CARD_SELECTOR) == null) continue;
    // CSS（subgrid）が揃える領域は測らない
    if (isCssAligned(element)) continue;
    found.push(element);
  }

  return found;
}

/**
 * 内容の指紋。これが変わらなければ自然高さも変わらないので測り直さない。
 *
 * `clientWidth` はレイアウトの読みだが、パスの先頭（まだ何も書いていない時点）で
 * まとめて読むので、走るレイアウトは1回だけ。
 *
 * 幅 0 は「表示されていない」を意味する。ランキングのカテゴリタブのように
 * 非表示のカードが同時に存在する場合、高さ 0 で計測して書き込むと
 * タブを切り替えた瞬間に潰れたままになるので、対象から外す。
 * 表示に切り替わると幅が変わって指紋も変わるため、そこで測り直される。
 *
 * @param {Element} element
 * @returns {{width: number, signature: string}}
 */
function signatureOf(element) {
  const width = element.clientWidth;

  return {
    width,
    signature: `${width}|${element.childElementCount}|${element.textContent?.length ?? 0}`,
  };
}

/**
 * 対象を集める。読みも書きもしない。
 * @returns {{tags: Element[], prices: Element[], rankingCards: Element[]}}
 */
function collectTargets() {
  return {
    tags: cardTargets(TAGS_SELECTOR),
    // `product-price` は商品ページ本体にも出るので、カード内のものだけを対象にする
    prices: cardTargets(PRICE_SELECTOR),
    rankingCards: cardTargets(RANKING_CARD_SELECTOR),
  };
}

/** ランキングカードの min-height 書き込み値 @type {WeakMap<Element, string>} */
const appliedMinHeight = new WeakMap();

/**
 * W1: 指定を全部外して自然な状態に戻す。
 *
 * CSS 側の max-height は JS が制御するあいだ無効にする
 * （上限で押さえられると最終行の枠線が切れる）。
 *
 * @param {{tags: Element[], prices: Element[], rankingCards: Element[]}} targets
 */
function resetAll({ tags, prices, rankingCards }) {
  for (const element of tags) {
    const style = /** @type {HTMLElement} */ (element).style;
    style.removeProperty('height');
    style.maxHeight = 'none';
    appliedHeight.delete(element);
  }

  for (const element of prices) {
    const style = /** @type {HTMLElement} */ (element).style;
    style.removeProperty('height');
    style.removeProperty('padding-top');
    appliedHeight.delete(element);
  }

  for (const card of rankingCards) {
    /** @type {HTMLElement} */ (card).style.removeProperty('min-height');
    appliedMinHeight.delete(card);
  }
}

/**
 * R1: リセット後のレイアウトで、タグ枠と価格枠をまとめて測る。
 *
 * タグ枠は padding を含めた必要高さが必要なので scrollHeight を使う
 * （getBoundingClientRect はパディング込みの現在の箱で、内容がはみ出していても気づけない）。
 * 最大行数の上限は CSS ではなくここで掛ける。CSS の max-height で押さえると
 * 適用値が上限に切られて最終行の枠線が消えるため。
 *
 * 価格枠は「揃えたいのが枠の上端でも下端でもなく販売価格の行」なので、
 *   上に必要な余白 = 行内の最大 offset（枠上端 → 販売価格行）
 *   下に必要な高さ = 行内の最大 (自然高さ - offset)
 * の材料として offset と自然高さの両方を取る。
 *
 * @param {{tags: Element[], prices: Element[]}} targets
 * @returns {{tagHeights: Map<Element, number>, priceMetrics: Map<Element, {offset: number, height: number}>}}
 */
function measureTagsAndPrices({ tags, prices }) {
  // 行のグルーピングに使うカード上端を、書き込みの前にまとめて読んでキャッシュに載せる。
  // W2 のあとで読み直すと、タグ枠の高さを書いた後の座標が混ざって行の判定が壊れる
  // （同じパスの中で古い座標と新しい座標を比較してしまう）。
  for (const element of tags) topOf(cardOf(element));
  for (const element of prices) topOf(cardOf(element));

  /** @type {Map<Element, number>} */
  const tagHeights = new Map();
  for (const element of tags) {
    tagHeights.set(element, Math.min(element.scrollHeight, capOf(element)));
  }

  /** @type {Map<Element, {offset: number, height: number}>} */
  const priceMetrics = new Map();
  for (const element of prices) {
    const line = element.querySelector('.price--sale, .price');
    const offset = line
      ? line.getBoundingClientRect().top - element.getBoundingClientRect().top
      : 0;

    priceMetrics.set(element, { offset, height: element.scrollHeight });
  }

  return { tagHeights, priceMetrics };
}

/**
 * W2: 行ごとの最大値をタグ枠・価格枠に書く。
 *
 * @param {{tags: Element[], prices: Element[]}} targets
 * @param {{tagHeights: Map<Element, number>, priceMetrics: Map<Element, {offset: number, height: number}>}} measured
 */
function applyTagsAndPrices({ tags, prices }, { tagHeights, priceMetrics }) {
  for (const bucket of groupByRow(tags)) {
    let max = 0;
    for (const element of bucket) max = Math.max(max, tagHeights.get(element) ?? 0);

    // タグの枠線は 0.5px なので、実測値そのままだと端数の丸めで最終行の下線が切れる。
    // CSS 側の padding-bottom 3px と合わせて下線とクリップ境界の間に計5pxの余裕を作る。
    // 注意: この余裕 + padding-bottom が行間ギャップ(6px)以上になると、
    // 行数超過でクリップされた次行タグの上辺が覗くので 2px より増やさないこと。
    const height = `${Math.ceil(max) + 2}px`;
    for (const element of bucket) setHeight(element, height);
  }

  for (const bucket of groupByRow(prices)) {
    let maxOffset = 0;
    let maxBelow = 0;

    for (const element of bucket) {
      const entry = priceMetrics.get(element);
      if (!entry) continue;
      maxOffset = Math.max(maxOffset, entry.offset);
      maxBelow = Math.max(maxBelow, entry.height - entry.offset);
    }

    // `scrollHeight` は整数に丸められるので、実際の内容（例 38.375px）より小さい値
    // （38px）が返る。その値をそのまま height にすると内容が数百分の1px はみ出し、
    // `.product-card__content` の overflow: hidden に切られて -XX% バッジの下枠線が消える。
    // タグ枠と同じ考え方で 1px の余裕を持たせる（行内の全カードに同じ値を書くので
    // 販売価格行とカラードットの位置揃えは崩れない）。
    const height = `${Math.ceil(maxOffset + maxBelow) + 1}px`;

    for (const element of bucket) {
      const entry = priceMetrics.get(element);
      const style = /** @type {HTMLElement} */ (element).style;

      style.boxSizing = 'border-box';
      if (entry) style.paddingTop = `${Math.max(0, Math.round(maxOffset - entry.offset))}px`;
      setHeight(element, height);
    }
  }
}

/**
 * R2 + W3: ランキング（パネル横断スワイプ）ではカード自体の高さも段ごとに揃える。
 *
 * タグ・価格を揃えてもスウォッチ行数（0〜2行）の差でカード高さが変わり、
 * 次の段の開始位置が隣のパネルとズレて継ぎ目で崩れて見えるため。
 *
 * **W2 の書き込み結果を含んだカード高さが必要なので、ここは必ずタグ・価格の適用後に走る。**
 * 段の判定は上端Yで行う（段間は約280px空くので許容40pxで安全に束ねられる）。
 *
 * @param {Element[]} cards
 */
function alignRankingCards(cards) {
  if (cards.length === 0) return;

  /** @type {Map<Element, number>} */
  const heights = new Map();
  for (const card of cards) {
    const rect = card.getBoundingClientRect();
    cardTopCache.set(card, rect.top + window.scrollY);
    heights.set(card, rect.height);
  }

  for (const bucket of groupByRow(cards, RANKING_ROW_TOLERANCE)) {
    let max = 0;
    for (const card of bucket) max = Math.max(max, heights.get(card) ?? 0);

    const minHeight = `${Math.ceil(max)}px`;
    for (const card of bucket) {
      if (appliedMinHeight.get(card) === minHeight) continue;
      /** @type {HTMLElement} */ (card).style.minHeight = minHeight;
      appliedMinHeight.set(card, minHeight);
    }
  }
}

/**
 * 測り直しが必要な要素を選ぶ。
 *
 * 行の最大高さを求めるには行の全要素が必要なので、行に1つでも測り直しが必要なら
 * その行はまとめて対象にする。全部そのままの行は触らない（リセットも書き込みもしない）。
 *
 * @param {Element[]} elements
 * @param {Set<Element>} dirty
 * @returns {Element[]}
 */
function elementsInDirtyRows(elements, dirty) {
  if (dirty.size === 0) return [];

  const target = [];
  for (const bucket of groupByRow(elements)) {
    if (bucket.some((element) => dirty.has(element))) target.push(...bucket);
  }

  return target;
}

function align() {
  beginPass();

  const targets = collectTargets();

  if (targets.tags.length === 0 && targets.prices.length === 0 && targets.rankingCards.length === 0) {
    resizedCards.clear();
    return;
  }

  // R0: 内容の指紋を読む。まだ何も書いていないのでレイアウトは1回だけ走る。
  // 幅 0（= 非表示）はここで落とす。
  /** @type {Map<Element, string>} */
  const signatures = new Map();
  /** @type {Set<Element>} */
  const dirty = new Set();

  /**
   * @param {Element[]} elements
   * @returns {Element[]}
   */
  const screen = (elements) => {
    /** @type {Element[]} */
    const visible = [];

    for (const element of elements) {
      const { width, signature } = signatureOf(element);
      if (width === 0) continue;

      visible.push(element);
      signatures.set(element, signature);

      if (
        forceRemeasure ||
        !appliedHeight.has(element) ||
        measuredSignature.get(element) !== signature ||
        resizedCards.has(cardOf(element))
      ) {
        dirty.add(element);
      }
    }

    return visible;
  };

  const visibleTags = screen(targets.tags);
  const visiblePrices = screen(targets.prices);
  const visibleRanking = targets.rankingCards.filter((card) => card.clientWidth > 0);

  // ランキングのカード高さはタグ枠・価格枠の高さを含むので、そちらが動くなら測り直す。
  const rankingDirty =
    visibleRanking.length > 0 &&
    (dirty.size > 0 ||
      visibleRanking.some((card) => !appliedMinHeight.has(card) || resizedCards.has(card)));

  resizedCards.clear();

  // 何も変わっていないパスは、ここで書き込みゼロで終わる。
  // 保険のタイマーや fonts.ready で走る2回目以降はほぼこれになる。
  if (dirty.size === 0 && !rankingDirty) return;

  const scopedTags = appendOnly ? elementsInPendingRows(visibleTags) : visibleTags;
  const scopedPrices = appendOnly ? elementsInPendingRows(visiblePrices) : visiblePrices;

  const pending = {
    tags: elementsInDirtyRows(scopedTags, dirty),
    prices: elementsInDirtyRows(scopedPrices, dirty),
    rankingCards: rankingDirty ? visibleRanking : [],
  };

  // W1 → R1 → W2
  resetAll(pending);
  invalidateReads();
  const measured = measureTagsAndPrices(pending);
  applyTagsAndPrices(pending, measured);

  // 測った内容の指紋を残す。次のパスで同じなら丸ごと省ける。
  // 指紋はリセット前（= 高さを書く前）の値なので、書き込みで変わることはない。
  for (const element of pending.tags) measuredSignature.set(element, signatures.get(element) ?? '');
  for (const element of pending.prices) measuredSignature.set(element, signatures.get(element) ?? '');

  // R2 → W3
  invalidateReads();
  alignRankingCards(pending.rankingCards);

  // 自分の style 書き込みで監視が再発火して無限ループになるのを防ぐ
  observer?.takeRecords();
}

function run() {
  frame = null;
  timer = null;
  lastRun = performance.now();
  align();
  appendOnly = false;
  forceRemeasure = false;
}

function schedule() {
  if (frame != null || timer != null) return;

  const since = performance.now() - lastRun;

  if (since >= MIN_INTERVAL) {
    frame = requestAnimationFrame(run);
    return;
  }

  // 直前に走ったばかりなら、間隔を空けてから1回だけ走らせる
  timer = setTimeout(() => {
    timer = null;
    frame = requestAnimationFrame(run);
  }, MIN_INTERVAL - since);
}

/**
 * 揃えの対象（カード内のタグ枠・価格枠）そのものか、それを子孫に持つか。
 *
 * ReviCo のレビューカルーセルのように、カードと無関係な DOM を遅延で大量に注入する
 * サードパーティがあると、変化のたびに全カードの再計測が走る。
 * 実測（PRD・CPU 4倍スロットリング）では ReviCo がレビュー20枚を注入するだけで
 * このスクリプトの強制レイアウト読みが 692回・TBT 550ms 増えていた
 * （ReviCo 自身の読みは rect 1回・getComputedStyle 62回）。
 * 揃えに関係しない変化はここで捨てる。
 *
 * @param {Node} node
 * @returns {boolean}
 */
function affectsAlignment(node) {
  if (!(node instanceof Element)) return false;

  // CSS が揃える領域（コレクション / 検索のグリッド）の中の変化は無視する。
  // Swym のボタン注入や無限スクロールの追加はここで止まり、整列パス自体が走らない。
  if (isCssAligned(node)) return false;

  // カードそのもの（グリッドの項目・カルーセルの項目）
  if (node.matches(CARD_SELECTOR)) return true;

  // カード内のタグ枠・価格枠
  if (node.matches(ALIGN_TARGET_SELECTOR) && node.closest(CARD_SELECTOR) != null) return true;

  // 子孫にカードを持つ（絞り込みの再描画、ランキングのパネル、閲覧履歴の注入など）
  if (node.querySelector(CARD_SELECTOR) != null) return true;

  // カード外の価格表示（カートドロワー等）を拾わないよう、対象がカード内にあることまで見る
  const target = node.querySelector(ALIGN_TARGET_SELECTOR);
  return target != null && target.closest(CARD_SELECTOR) != null;
}

/**
 * Swym が注入したノードか。
 * 削除されたノードは親を辿れないので、自身のクラス名も見る。
 * @param {Node} node
 * @returns {boolean}
 */
function isSwymNode(node) {
  if (!(node instanceof Element)) return false;
  if (typeof node.className === 'string' && node.className.includes('swym-')) return true;

  // 祖先を辿る範囲はカードの内側までに限る。
  // Swym は読み込み完了後に <body> へ `swym-ready` / `swym-buttons-loaded` を付けるため、
  // 無条件に closest すると **ページ上の全要素が Swym ノード扱い**になり、以降の DOM 変化が
  // すべて捨てられて再整列が止まる。実測では無限スクロールで追加されたカード
  // （コレクション一覧の 372 枚中 246 枚）に高さが一度も適用されていなかった。
  const swymAncestor = node.closest?.('[class*="swym-"]');
  if (swymAncestor == null) return false;

  const scope = node.closest(CARD_SELECTOR) ?? node.parentElement;
  return scope != null && scope.contains(swymAncestor);
}

/**
 * 行揃えに関係しない変化を弾く。
 *
 * Swym のボタンはカード内で absolute 配置されるためタグ枠・価格枠の高さを変えない。
 * にもかかわらずカード枚数ぶんの変化が飛んでくるので、ここで捨てないと
 * 再計算がカード枚数ぶん連鎖する。
 *
 * @param {MutationRecord} mutation
 * @returns {boolean}
 */
function isIgnorable(mutation) {
  if (mutation.type === 'attributes') {
    if (isSwymNode(mutation.target)) return true;

    // 表示の切り替えは、カード自身か、カードを含む要素の class / hidden / aria-* に出る。
    // 揃え対象を持たない要素の属性変化は無視する。
    // 特にカード内の画像スライド（`slideshow-slide`）は aria-hidden をひんぱんに切り替えるが
    // タグ枠・価格枠の高さには影響しない。実測ではコレクション一覧1ページで 763 回発生し、
    // 全カードの測り直しを 23 回も引き起こしていた。
    const target = mutation.target;
    if (!(target instanceof Element)) return true;
    return !affectsAlignment(target);
  }

  const changed = [...mutation.addedNodes, ...mutation.removedNodes].filter(
    (node) => node instanceof Element
  );

  if (changed.length === 0) return true;

  // Swym のボタン注入、および揃え対象を含まない注入（ReviCo のレビューカルーセルなど）は捨てる。
  // 削除されたノードは親を辿れないので、ノード自身と子孫だけで判定する。
  return changed.every((node) => isSwymNode(node) || !affectsAlignment(node));
}

/**
 * カードが足されただけの変化か。
 *
 * 無限スクロールの追加読み込みがこれに当たる。既存カードの差し替え（絞り込み・morph）や
 * 削除を含む場合は、揃え済みの行の内容も変わっているので全体を測り直す必要がある。
 *
 * @param {MutationRecord} mutation
 * @returns {boolean}
 */
function isAppendOnly(mutation) {
  if (mutation.type !== 'childList') return false;
  if (mutation.removedNodes.length > 0) return false;

  const added = [...mutation.addedNodes].filter((node) => node instanceof Element);
  if (added.length === 0) return false;

  // カードそのもの（グリッドの項目）が足された場合だけ。カード内部への注入は対象外
  return added.every((node) => node.matches(CARD_SELECTOR));
}

function init() {
  schedule();

  window.addEventListener('resize', schedule, { passive: true });

  // 画像のデコード完了でカードの高さが変わると行の境界がずれるため、
  // 読み込み完了でもう一度合わせる（takeRecords 直後の取りこぼし対策）。
  window.addEventListener('load', schedule);

  // 絞り込み・ページネーション・閲覧履歴の非同期描画で差し替わるため監視する。
  // ランキングのカテゴリタブは DOM の入れ替えではなく表示切り替えなので、
  // 属性の変化も拾わないと切り替え後の高さが更新されない。
  observer = new MutationObserver((mutations) => {
    const relevant = mutations.filter((mutation) => !isIgnorable(mutation));
    if (relevant.length === 0) return;

    // 追加だけ（削除や差し替えを含まない）なら、未処理の行だけを揃える
    if (relevant.every(isAppendOnly)) appendOnly = true;
    else appendOnly = false;

    schedule();
  });

  // `style` は監視しない。自分と Swym の書き込みで往復するだけで、
  // 表示の切り替えは class / hidden / aria-* / data-active 側に出る。
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'hidden', 'aria-hidden', 'aria-selected', 'data-active'],
  });

  // MutationObserver は「高さだけが変わる」変化（画像の読み込み、フォントの差し替え、
  // 折り返しの増減）を拾えない。ResizeObserver で実寸の変化を直接見る。
  //
  // ただしカードの寸法は **自分の書き込みでも変わる**（タグ枠・価格枠の高さ、
  // ランキングの min-height）。素通しにすると「書く → 通知 → また整列」で走り続けるので、
  // 観測した寸法が前回と同じなら何もしない。書いた値が前回と同じに落ち着けば通知は止まる。
  // contentRect はコールバックの引数なので、読んでもレイアウトは走らない。
  if (typeof ResizeObserver !== 'undefined') {
    /** @type {WeakMap<Element, string>} */
    const lastSize = new WeakMap();

    resizeObserver = new ResizeObserver((entries) => {
      let changed = false;

      for (const entry of entries) {
        const size = `${Math.round(entry.contentRect.width)}x${Math.round(entry.contentRect.height)}`;
        if (lastSize.get(entry.target) === size) continue;
        lastSize.set(entry.target, size);
        // 指紋（幅・子要素数・文字数）では拾えない変化なので、このカードは測り直す
        resizedCards.add(entry.target);
        changed = true;
      }

      if (changed) schedule();
    });

    // 非表示のカードも監視する。ランキングのタブが切り替わって表示に変わった瞬間を
    // ここで拾える（`clientWidth` が 0 から実寸に変わるので指紋も変わる）。
    for (const element of cardTargets(CARD_SELECTOR)) resizeObserver.observe(element);
  }

  // Web フォント読み込み後に文字幅が変わって行数が増減するケースに追従する。
  // 指紋は文字幅の変化を拾えないので、このパスに限り全量を測り直す。
  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      forceRemeasure = true;
      schedule();
    });
  }

  // 保険。アプリの遅延描画（Swym のボタン注入など）で高さが動くことがある。
  for (const ms of [400, 1200, 3000]) setTimeout(schedule, ms);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
